import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { User } from "../types";

interface InviteInfo {
  valid: boolean;
  reason?: string;
  householdName?: string;
  email?: string | null;
}

export default function AcceptInvitePage({ user }: { user: User | null }) {
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [done, setDone] = useState(false);

  const info = useQuery({
    queryKey: ["invite", token],
    queryFn: () => api.get<InviteInfo>(`/api/household/accept?token=${encodeURIComponent(token)}`),
  });

  const accept = useMutation({
    mutationFn: () => api.post("/api/household/accept", { token }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["me"] });
      await qc.invalidateQueries({ queryKey: ["household"] });
      setDone(true);
      setTimeout(() => navigate("/"), 1200);
    },
  });

  useEffect(() => {
    if (!user) return;
    if (info.data?.valid && !done) {
      accept.mutate();
    }
  }, [user, info.data?.valid]);

  if (info.isLoading) return <Centered>Načítám pozvánku…</Centered>;

  if (!info.data?.valid) {
    return (
      <Centered>
        <div aria-hidden className="text-4xl mb-2">⚠️</div>
        <h1 className="text-xl font-bold mb-2">Pozvánka není platná</h1>
        <p className="text-sm text-slate-500 mb-4">
          {info.data?.reason === "expired"
            ? "Tato pozvánka vypršela."
            : info.data?.reason === "used"
              ? "Tato pozvánka už byla použita."
              : "Pozvánka nebyla nalezena."}
        </p>
        <a className="btn-secondary" href="/">
          Zpět na hlavní stránku
        </a>
      </Centered>
    );
  }

  if (!user) {
    const next = `/rodina/pozvanka/${token}`;
    return (
      <Centered>
        <div aria-hidden className="text-4xl mb-2">✉️</div>
        <h1 className="text-xl font-bold mb-2">Pozvánka do domácnosti</h1>
        <p className="text-sm text-slate-500 mb-4">
          Byli jste pozváni do <strong>{info.data.householdName}</strong>. Pro připojení se přihlaste přes Google.
        </p>
        <a className="btn-primary" href={`/api/auth/google/start?next=${encodeURIComponent(next)}`}>
          Přihlásit se přes Google
        </a>
      </Centered>
    );
  }

  return (
    <Centered>
      <div aria-hidden className="text-4xl mb-2">{done ? "🎉" : "⏳"}</div>
      <h1 className="text-xl font-bold mb-2">
        {done ? "Úspěšně připojeno" : "Připojuji k domácnosti…"}
      </h1>
      <p className="text-sm text-slate-500">
        {done ? `Jste teď členem ${info.data.householdName}.` : "Chvíli strpení."}
      </p>
    </Centered>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="card card-body max-w-md w-full text-center">{children}</div>
    </div>
  );
}
